import _ from 'lodash';
import MapUtil from 'gw-portals-util-js/MapUtil';
import Typelists from './_TypelistDefinition';
import Dtos from './_DtoDefinition';
import StringUtil from 'gw-portals-util-js/StringUtil';

const metaTypeNames = ['dto', 'typelist'];
const metaTypes = {};

metaTypeNames.forEach((metaType) => {
    const metaObject = {
        kind: metaType
    };
    metaTypeNames.forEach((possibleMetaType) => {
        metaObject[`is${StringUtil.capitalizeFirstLetter(possibleMetaType)}`] = possibleMetaType === metaType;
    });
    metaTypes[metaType] = Object.freeze(metaObject);
});

const typeFactories = {
    'edge.metadata.typeinfo.dtoinfo.dto.DTOMetadataDTO': (typeRef, xCenter, translator, typeName, typeMeta) => {
        return Dtos.createDTOInfo(xCenter, typeRef, typeName, typeMeta.value, metaTypes.dto);
    },
    'edge.metadata.typeinfo.typelistinfo.dto.TypelistMetadataDTO': (typeRef, xCenter, translator, typeName, typeMeta) => {
        return Typelists.createTypelistInfo(xCenter, translator, typeName, typeMeta.value, metaTypes.typelist);
    }
};

/* Parses a type metadata. */
function parseType(typeRef, xCenter, translator, typeMeta, typeName) {
    const typeKindTranslator = MapUtil.get(typeFactories, typeMeta.type);
    if (!typeKindTranslator) {
        throw new Error(`Type metadata format ${typeMeta.type} was not recognized as a valid type metadata.`);
    }
    return typeKindTranslator(typeRef, xCenter, translator, typeName, typeMeta);
}

/* Checks if type have a specific (meta-)type. */
function hasKind(kind, typeObject) {
    return typeObject.metaType.kind === kind;
}

export default {
    /**
     * Creates a representation of types belonging to an xcenter.
     *
     * Following properties and functions are defined on the object:
     * <dl>
     *     <dt>allTypes</dt><dd>List of all types defined for the xcenter</dd>
     *     <dt>dtoTypes</dt><dd>List of all DTO types defined for the xcenter</dd>
     *     <dt>typelists</dt><dd>List of typelists defined for the xcenter</dd>
     *     <dt>has([typeKind], typeName)</dt><dd>Checks if typeName is defined for the types. If typekind is passed,
     *       checks type presense of the specific kind.</dd>
     *     <dt>get([typeKind], typeName)</dt><dd>Retrieves a type by its name. Throws Error if type was not found.
     *       Searches for specific type kinds if two-arg version is used.
     *     <dt>getTypelist(typelistNameOrType)</dt><dd>Retrieves a typelist by either a type name or simple typelist
     *       name. Throws Error if typelist is not present in the xcenter</dd>
     * </dl>
     *
     * @param {function} translator translation function.
     * @param {Object} typesMeta information about types.
     * @param {*} xCenter reference back to an xCenter.
     *
     * @returns {Object}
     */
    'createXCenterTypes': (translator, typesMeta, xCenter) => {
        let typeMap = {};

        function getTypeRef(typeName) {
            if (!_.has(typesMeta, typeName)) {
                throw new Error(`Illegal reference to unknown type ${typeName}`);
            }
            return () => {
                return typeMap[typeName];
            };
        }

        typeMap = _.mapValues(typesMeta, _.partial(parseType, getTypeRef, xCenter, translator));

        const _get = function () {
            let guess;
            if (arguments.length === 2) {
                guess = MapUtil.get(typeMap, arguments[1]);
                if (!guess || !hasKind(arguments[0], guess)) {
                    throw new Error(`No ${arguments[0]} with name ${arguments[1]} is defined`);
                }
                return guess;
            }
            guess = MapUtil.get(typeMap, arguments[0]);
            if (!guess) {
                throw new Error(`No type with name ${arguments[0]} is defined`);
            }
            return guess;
        };

        return {
            'allTypes': _.values(typeMap),
            'dtoTypes': _.values(typeMap).filter(_.partial(hasKind, 'dto')),
            'typelists': _.values(typeMap).filter(_.partial(hasKind, 'typelist')),
            'has'() {
                if (arguments.length === 2) {
                    return _.has(typeMap, arguments[1]) && hasKind(arguments[0], typeMap[arguments[1]]);
                }
                return _.has(typeMap, arguments[0]);
            },
            'get': _get,
            'getTypelist': (typelistNameOrType) => {
                /* Convert simple typelist name into the type name. */
                if (typelistNameOrType.indexOf('.') < 0) {
                    typelistNameOrType = `typekey.${typelistNameOrType}`;
                }
                return _get('typelist', typelistNameOrType);
            }
        };
    }
};